import { QueryClient } from '@tanstack/react-query';
import { getSupabaseClient } from '@/integrations/supabase/client';
import { supabaseManager } from '@/services/supabaseManager';
import { tabsService } from '@/services/tabs/tabsService';
import { tabSplitService } from '@/services/tabs/tabSplitService';
import { productInventoryService } from '@/services/products';
import { salesService } from '@/services/sales/salesService';
import type { SaleSellerRef } from '@/services/sales/resolveSaleSeller';
import {
  createElectronicInvoice,
  getCompanySimplifiedRegime,
} from '@/services/hacienda/haciendaService';
import {
  createElectronicTicketParams,
  getHaciendaPaymentCodeById,
  HACIENDA_PAYMENT_CONDITION_CASH,
} from '@/services/hacienda/haciendaHelpers';
import { queryKeys } from '@/lib/queryKeys';
import { getQueryClient } from '@/lib/queryClientHolder';
import { STALE_TIME_REFERENCE } from '@/lib/queryCacheConfig';
import {
  calculateWeightLinePrice,
  hasPositiveWeight,
  isWeightLine,
  isWeightPartialMove,
  splitWeightLine,
  WEIGHT_EPS,
} from '@/utils/weightPricing';
import { generateUuid } from '@/lib/uuid';
import { STORAGE_KEYS } from '@/services/supabase/config';
import { resolvePromotionComponentsForDispatch } from '@/services/promotions/promotionDispatch';
import { dispatchReprintService } from '@/services/invoice/dispatchReprintService';
import type { DispatchPrintTab } from '@/types/invoice';
import type {
  Product,
  ProductPromotion,
  PromotionSelectionInput,
  SelectedProductModifier,
  Tab,
  TabItem,
  TabItemPromotionComponent,
} from '@/types';
import { lineUnitPrice, modifierSignature, toTabItemModifiers } from '@/lib/productModifiers';
import { sendSupportEmail } from '@/services/supportEmail/supportEmailService';
import { getCurrentOrganization } from '@/lib/organizationBridge';

/**
 * Offline-capable POS mutations.
 *
 * Core write operations use client-generated ids and self-contained variables so
 * TanStack Query can pause them while offline and replay them later — including
 * after an app restart. For a mutation to be replayable after restart, its
 * `mutationFn` must be registered via `setMutationDefaults` and referenced by
 * `mutationKey` (inline functions can't be rehydrated from disk). Hooks provide
 * their own UI callbacks (toasts, optimistic cache updates) on top; the defaults
 * here are what run for mutations restored from the persisted cache.
 */

export const offlineMutationKeys = {
  createTab: ['offline', 'createTab'] as const,
  addItemsToTab: ['offline', 'addItemsToTab'] as const,
  moveItemsBetweenTabs: ['offline', 'moveItemsBetweenTabs'] as const,
  recordSaleAndCloseTab: ['offline', 'recordSaleAndCloseTab'] as const,
  createElectronicTicket: ['offline', 'createElectronicTicket'] as const,
};

export interface CreateTabVariables {
  /** Client-generated UUID — also used as the optimistic cache id. */
  tabId: string;
  customerName: string;
  userId: string;
  organizationId: string;
  tableId?: string | null;
  /** Original creation time; preserved when the mutation replays later. */
  createdAtIso: string;
  isQuickSale?: boolean;
  /** Operator who opened the tab; stored once so later checkout is not attributed to the cashier. */
  serverName?: string;
  /** Initial tab discount percentage (0-100). */
  discount?: number;
}

export interface AddItemsToTabVariables {
  tabId: string;
  organizationId: string;
  items: Array<{
    /** Client-generated UUID used when the item results in a new tab_items row. */
    itemId: string;
    product: Product;
    quantity: number;
    notes?: string;
    customerPosition?: number;
    promotionSelections?: PromotionSelectionInput[];
    /** Snapshot at enqueue time so a replay charges what the cashier saw. */
    modifiers?: SelectedProductModifier[];
    /** Weight in kg for sold-by-weight lines. */
    weightKg?: number;
  }>;
  /** Passed through to productInventoryService; kitchen tickets are only created when true. */
  createKitchenTickets?: boolean;
  dispatchReprint?: DispatchPrintTab;
}

/**
 * Self-contained payload for paying/closing a tab. Everything the server needs
 * is resolved at enqueue time (seller, totals, sale date) so the mutation can
 * pause offline and replay later — even after an app restart, when the tab
 * object and auth context are no longer around.
 */
export interface RecordSaleAndCloseTabVariables {
  /** Client-generated UUID for the sale. */
  saleId: string;
  tabId: string;
  organizationId: string;
  customerName: string;
  subtotal: number;
  /** Tab discount percentage (the service maps 100% to NULL like before). */
  discount: number;
  serviceCharge?: number;
  serviceChargeRate?: number;
  iva?: number;
  ivaRate?: number;
  tip?: number;
  total: number;
  /** Final JSONB payload, including the trailing totalDollarsReceived entry. */
  paymentMethodsJson: Array<{ methodId: string; amount: number } | { totalDollarsReceived: number }>;
  sinpePhotoUrl?: string;
  cardLastFourDigits?: string;
  isQuickSale?: boolean;
  /** Resolved at enqueue time; seller lookup queries the DB and can't run offline. */
  seller: SaleSellerRef;
  /** Time of payment (with the cash-closure day shift applied), preserved on replay. */
  saleDateIso: string;
}

/**
 * Outbox payload for a tiquete electrónico. Snapshotted at close time; the
 * local gates (feature flag, org setting, IVA, supported payment method) are
 * checked before enqueueing, while the regime check and the idempotency guard
 * run in the executor, which is online by definition.
 */
export interface CreateElectronicTicketVariables {
  saleId: string;
  organizationId: string;
  items: TabItem[];
  tabDiscount: number;
  currency: string;
  defaultPaymentMethodId?: number;
  serviceCharge?: number;
  serviceChargeRate?: number;
  tip?: number;
}

/**
 * Self-contained payload for moving items between open tabs. Snapshotted from
 * the tabs cache at enqueue time so the mutation can pause offline and replay
 * without re-reading tab_items / promotion components from the DB.
 */
export interface MoveItemsBetweenTabsVariables {
  organizationId: string;
  sourceTabId: string;
  targetTabId: string;
  items: Array<{
    itemId: string;
    quantityToMove: number;
    /** Quantity on the source row at enqueue time (for absolute partial reduce). */
    sourceQuantity: number;
    isFullMove: boolean;
    newItemId?: string;
    productId: string;
    productName: string;
    productCategory: string;
    categoryId?: string | null;
    unitPrice: number;
    discount: number;
    notes?: string | null;
    customerPosition?: number | null;
    weightKg?: number | null;
    remainingWeightKg?: number | null;
    remainingUnitPrice?: number | null;
    promotionComponents?: Array<{
      componentProductId: string;
      quantity: number;
      productName: string;
    }>;
    modifiers?: Array<{
      modifierId: string | null;
      name: string;
      price: number;
    }>;
  }>;
}

export interface MoveItemsBetweenTabsInput {
  sourceTabId: string;
  targetTabId: string;
  itemsToMove: Array<{
    itemId: string;
    quantityToMove?: number;
    weightKgToMove?: number;
  }>;
}

const throwMoveValidation = (message: string): never => {
  const error = new Error(message);
  error.name = 'MOVE_VALIDATION_ERROR';
  throw error;
};

/** Builds a replay-safe add-items payload for a single product. */
export const buildAddItemVariables = (
  tabId: string,
  organizationId: string,
  product: Product,
  quantity = 1,
  notes?: string,
  promotionSelections?: PromotionSelectionInput[],
  weightKg?: number
): AddItemsToTabVariables => ({
  tabId,
  organizationId,
  items: [{ itemId: generateUuid(), product, quantity, notes, promotionSelections, weightKg }],
});

/**
 * Tracks in-flight tab creations by tab id. Dependent operations (adding items)
 * await the creation before writing rows that reference the tab, avoiding FK
 * violations when a user adds items right after opening a tab. When mutations
 * are replayed after a restart this map is empty, but `resumePausedMutations`
 * runs them serially in original order, which preserves the same guarantee.
 */
const pendingTabCreations = new Map<string, Promise<void>>();

export const waitForPendingTabCreation = async (tabId: string): Promise<void> => {
  const pending = pendingTabCreations.get(tabId);
  if (pending) {
    await pending;
  }
};

/**
 * Ensures the org Supabase client is connected before a replayed mutation runs.
 * After an offline cold start the client may not be connected yet when the
 * network returns and paused mutations resume.
 */
export const ensureOrganizationClient = async (): Promise<void> => {
  try {
    getSupabaseClient();
    return;
  } catch {
    const slug = localStorage.getItem(STORAGE_KEYS.currentOrganizationSlug);
    if (!slug) {
      throw new Error('No organization connection available for offline mutation replay');
    }
    await supabaseManager.connectToOrganizationBySlug(slug);
  }
};

export const executeCreateTab = async (variables: CreateTabVariables): Promise<Tab> => {
  let release: () => void = () => undefined;
  const pending = new Promise<void>((resolve) => {
    release = resolve;
  });
  pendingTabCreations.set(variables.tabId, pending);

  try {
    await ensureOrganizationClient();
    return await tabsService.createTab({
      tabId: variables.tabId,
      customerName: variables.customerName,
      userId: variables.userId,
      organizationId: variables.organizationId,
      tableId: variables.tableId,
      createdAtIso: variables.createdAtIso,
      isQuickSale: variables.isQuickSale,
      serverName: variables.serverName,
      discount: variables.discount,
    });
  } finally {
    release();
    pendingTabCreations.delete(variables.tabId);
  }
};

/**
 * Tracks in-flight sale recordings by sale id so the electronic-ticket outbox
 * can wait for the sale row to exist before documenting it. After a restart,
 * serial replay order (sale enqueued before ticket) preserves the guarantee.
 */
const pendingSaleRecordings = new Map<string, Promise<void>>();
const pendingSaleReleasers = new Map<string, () => void>();

/**
 * Marks a sale recording as in flight. Call it synchronously when enqueuing
 * `recordSaleAndCloseTab`: TanStack runs that mutation's async `onMutate`
 * (optimistic update) before its `mutationFn`, so without an early
 * registration a sibling ticket mutation can reach its executor first, find
 * nothing to wait for and look up a sale row that isn't inserted yet.
 * Idempotent; the executor releases it in `finally`.
 */
export const registerPendingSaleRecording = (saleId: string): void => {
  if (pendingSaleRecordings.has(saleId)) return;

  let resolvePending: () => void = () => undefined;
  const pending = new Promise<void>((resolve) => {
    resolvePending = resolve;
  });

  pendingSaleRecordings.set(saleId, pending);
  pendingSaleReleasers.set(saleId, resolvePending);
};

export const releasePendingSaleRecording = (saleId: string): void => {
  pendingSaleReleasers.get(saleId)?.();
  pendingSaleReleasers.delete(saleId);
  pendingSaleRecordings.delete(saleId);
};

export const waitForPendingSaleRecording = async (saleId: string): Promise<void> => {
  const pending = pendingSaleRecordings.get(saleId);
  if (pending) {
    await pending;
  }
};

/**
 * Records the sale and closes the tab as one replayable unit. Split flows
 * (sale in one mutation, close in a per-call onSuccess) lose the close step if
 * the app restarts while offline; a composite mutation can't.
 */
export const executeRecordSaleAndCloseTab = async (
  variables: RecordSaleAndCloseTabVariables
) => {
  registerPendingSaleRecording(variables.saleId);

  try {
    await waitForPendingTabCreation(variables.tabId);
    await ensureOrganizationClient();

    const sale = await salesService.createSale({
      saleId: variables.saleId,
      tabId: variables.tabId,
      customerName: variables.customerName,
      subtotal: variables.subtotal,
      discount: variables.discount,
      serviceCharge: variables.serviceCharge,
      serviceChargeRate: variables.serviceChargeRate,
      iva: variables.iva,
      ivaRate: variables.ivaRate,
      tip: variables.tip,
      total: variables.total,
      paymentMethodsJson: variables.paymentMethodsJson,
      sinpePhotoUrl: variables.sinpePhotoUrl,
      cardLastFourDigits: variables.cardLastFourDigits,
      seller: variables.seller,
      organizationId: variables.organizationId,
      saleDateIso: variables.saleDateIso,
    });

    await tabsService.closeTab(variables.tabId);

    return sale;
  } finally {
    releasePendingSaleRecording(variables.saleId);
  }
};

const reportElectronicTicketFailure = async (
  saleId: string,
  message: string,
  error?: unknown
): Promise<void> => {
  const organization = getCurrentOrganization();
  try {
    await sendSupportEmail({
      subject: 'Error creating electronic ticket',
      message,
      source: 'offlineMutations.executeCreateElectronicTicket',
      level: 'error',
      error,
      metadata: {
        saleId,
        organizationId: organization?.id,
        organizationName: organization?.name,
        organizationSlug: organization?.slug,
      },
    });
  } catch (mailError) {
    console.error('Failed to send electronic ticket support email', mailError);
  }
};

/**
 * Creates the tiquete electrónico for a recorded sale. Failure-tolerant like
 * the pre-offline flow: a hacienda error resolves to null (sale keeps no
 * invoice_id) instead of throwing, so a microservice outage can't wedge the
 * offline queue. Idempotent on replay via the sale's invoice_id.
 */
export const executeCreateElectronicTicket = async (
  variables: CreateElectronicTicketVariables
): Promise<string | null> => {
  await waitForPendingSaleRecording(variables.saleId);
  await ensureOrganizationClient();

  const supabase = getSupabaseClient();
  const { data: saleRow, error } = await supabase
    .from('sales')
    .select('id, invoice_id')
    .eq('id', variables.saleId)
    .maybeSingle();
  if (error) throw error;

  // Sale never landed (replay failed upstream): nothing to document.
  if (!saleRow) {
    console.warn('Skipping electronic ticket: sale row not found', variables.saleId);
    await reportElectronicTicketFailure(
      variables.saleId,
      `Sale row not found for electronic ticket (${variables.saleId})`
    );
    return null;
  }
  // Replay guard: a previous attempt already issued the document.
  if (saleRow.invoice_id) return saleRow.invoice_id as string;

  // Regime comes from the Hacienda microservice (cached); tickets are only
  // issued for the traditional regime.
  const queryClient = getQueryClient();
  const isSimplifiedRegime = queryClient
    ? await queryClient.ensureQueryData({
        queryKey: queryKeys.companyRegime(variables.organizationId),
        queryFn: getCompanySimplifiedRegime,
        staleTime: STALE_TIME_REFERENCE.companyRegime,
      })
    : await getCompanySimplifiedRegime();
  if (isSimplifiedRegime) return null;

  const params = createElectronicTicketParams(
    variables.items,
    variables.currency,
    getHaciendaPaymentCodeById(variables.defaultPaymentMethodId),
    HACIENDA_PAYMENT_CONDITION_CASH,
    variables.tabDiscount,
    variables.serviceCharge,
    variables.serviceChargeRate,
    variables.tip
  );

  const response = await createElectronicInvoice(params);
  if (!response.success) {
    console.warn('Failed to create electronic ticket:', response.error);
    return null;
  }

  const invoiceId = response.data?.invoice_id;
  if (!invoiceId) {
    await reportElectronicTicketFailure(
      variables.saleId,
      `Electronic ticket response missing invoice_id for sale ${variables.saleId}`
    );
    return null;
  }

  try {
    await salesService.updateSaleInvoiceId(variables.saleId, invoiceId);
    return invoiceId;
  } catch (updateError) {
    console.error('Failed to store invoice_id for sale', updateError);
    await reportElectronicTicketFailure(
      variables.saleId,
      `Failed to store invoice_id ${invoiceId} for sale ${variables.saleId}`,
      updateError
    );
    return null;
  }
};

export const executeAddItemsToTab = async (variables: AddItemsToTabVariables) => {
  await waitForPendingTabCreation(variables.tabId);
  await ensureOrganizationClient();
  const result = await productInventoryService.addProductsToTabBulk({
    tabId: variables.tabId,
    organizationId: variables.organizationId,
    createKitchenTickets: variables.createKitchenTickets,
    products: variables.items.map(({ itemId, product, quantity, notes, customerPosition, promotionSelections, modifiers, weightKg }) => ({
      product,
      quantity,
      notes,
      customerPosition,
      promotionSelections,
      modifiers,
      clientItemId: itemId,
      weightKg,
    })),
  });

  if (variables.dispatchReprint) {
    try {
      await dispatchReprintService.createRecord(
        variables.organizationId,
        variables.tabId,
        variables.dispatchReprint,
      );
    } catch (error) {
      console.error('Failed to store dispatch reprint record:', error);
    }
  }

  return result;
};

/**
 * Builds a replay-safe move-items payload from the tabs cache. Validates source /
 * target open tabs and item quantities at enqueue time (not at replay).
 */
export const buildMoveItemsBetweenTabsVariables = (
  queryClient: QueryClient,
  organizationId: string,
  input: MoveItemsBetweenTabsInput
): MoveItemsBetweenTabsVariables => {
  const { sourceTabId, targetTabId, itemsToMove } = input;

  if (sourceTabId === targetTabId) {
    throwMoveValidation('Source and target tab must be different');
  }

  const tabs = queryClient.getQueryData<Tab[]>(queryKeys.tabs(organizationId)) ?? [];
  const sourceTab = tabs.find((tab) => tab.id === sourceTabId);
  const targetTab = tabs.find((tab) => tab.id === targetTabId);

  if (!sourceTab?.isOpen) {
    throwMoveValidation('Source tab not found or not open');
  }
  if (!targetTab?.isOpen) {
    throwMoveValidation('Target tab not found or not open');
  }

  const items: MoveItemsBetweenTabsVariables['items'] = itemsToMove.map((itemToMove) => {
    const { itemId, quantityToMove, weightKgToMove } = itemToMove;

    const sourceItem = sourceTab.items.find((item) => item.id === itemId);
    if (!sourceItem) {
      throwMoveValidation(`Item with id ${itemId} not found on source tab`);
    }

    const sourceWeightKg = isWeightLine(sourceItem) ? sourceItem.weightKg! : null;
    const categoryId =
      sourceItem.product.productCategory?.id || sourceItem.product.categoryId || null;

    const baseFields = {
      itemId,
      sourceQuantity: sourceItem.quantity,
      productId: sourceItem.productId,
      productName: sourceItem.product.name,
      productCategory: sourceItem.product.category,
      categoryId: categoryId || null,
      discount: sourceItem.discount,
      notes: sourceItem.notes ?? null,
      customerPosition: sourceItem.customerPosition ?? null,
      promotionComponents: sourceItem.promotionComponents?.map((component) => ({
        componentProductId: component.componentProductId,
        quantity: component.quantity,
        productName: component.productName,
      })),
      modifiers: sourceItem.modifiers?.map((modifier) => ({
        modifierId: modifier.modifierId,
        name: modifier.name,
        price: modifier.price,
      })),
    };

    if (sourceWeightKg != null) {
      if (
        typeof weightKgToMove !== 'number' ||
        !Number.isFinite(weightKgToMove) ||
        weightKgToMove <= 0
      ) {
        throwMoveValidation(`Invalid weightKgToMove for item ${itemId}`);
      }
      if (weightKgToMove > sourceWeightKg + WEIGHT_EPS) {
        throwMoveValidation(`Weight to move exceeds available weight for item ${itemId}`);
      }

      const split = splitWeightLine({
        unitPrice: sourceItem.unitPrice,
        sourceWeightKg,
        weightKgToMove,
      });

      if (split.isFullMove) {
        return {
          ...baseFields,
          quantityToMove: sourceItem.quantity,
          isFullMove: true,
          unitPrice: sourceItem.unitPrice,
          weightKg: sourceWeightKg,
        };
      }

      return {
        ...baseFields,
        quantityToMove: 1,
        isFullMove: false,
        newItemId: generateUuid(),
        unitPrice: split.movedUnitPrice,
        weightKg: split.movedWeightKg,
        remainingWeightKg: split.remainingWeightKg,
        remainingUnitPrice: split.remainingUnitPrice,
      };
    }

    if (!Number.isInteger(quantityToMove) || quantityToMove <= 0) {
      throwMoveValidation(`Invalid quantityToMove for item ${itemId}`);
    }
    if (quantityToMove > sourceItem.quantity) {
      throwMoveValidation(`Quantity to move exceeds available quantity for item ${itemId}`);
    }

    const isFullMove = quantityToMove >= sourceItem.quantity;
    return {
      ...baseFields,
      quantityToMove: isFullMove ? sourceItem.quantity : quantityToMove,
      isFullMove,
      newItemId: isFullMove ? undefined : generateUuid(),
      unitPrice: sourceItem.unitPrice,
      weightKg: null,
    };
  });

  return {
    organizationId,
    sourceTabId,
    targetTabId,
    items,
  };
};

export const executeMoveItemsBetweenTabs = async (
  variables: MoveItemsBetweenTabsVariables
): Promise<void> => {
  await waitForPendingTabCreation(variables.sourceTabId);
  await waitForPendingTabCreation(variables.targetTabId);
  await ensureOrganizationClient();
  return tabSplitService.moveItemsBetweenTabsFromSnapshots(variables);
};

/**
 * Applies an add-items payload to the cached tabs and products, mirroring the
 * service's merge rule (same product + notes + modifiers ⇒ merged quantity). Shared by
 * the single-add and staged-commit hooks so optimistic state stays consistent.
 * Returns the previous cache values for rollback.
 */
export const applyAddItemsOptimistically = (
  queryClient: QueryClient,
  variables: AddItemsToTabVariables
) => {
  const tabsKey = queryKeys.tabs(variables.organizationId);
  const productsKey = queryKeys.products(variables.organizationId);
  const externalOrdersKey = queryKeys.externalOrders(variables.organizationId);

  const previousTabs = queryClient.getQueryData<Tab[]>(tabsKey);
  const previousProducts = queryClient.getQueryData<Product[]>(productsKey);
  const previousExternalOrders = queryClient.getQueryData<Tab[]>(externalOrdersKey);

  const normalizeNotes = (notes?: string) => notes?.trim() || null;
  const normalizeCustomerPosition = (value?: number | null) =>
    typeof value === 'number' && value > 0 ? value : null;

  const promotions =
    queryClient.getQueryData<ProductPromotion[]>(queryKeys.promotions(variables.organizationId)) ?? [];
  const productNameById = new Map(
    (previousProducts ?? []).map((product) => [product.id, product.name] as const)
  );

  const resolveOptimisticPromotionComponents = (
    productId: string,
    promotionSelections?: PromotionSelectionInput[]
  ): TabItemPromotionComponent[] | undefined => {
    const promoDef = promotions.find((p) => p.promoProductId === productId && p.isActive);
    if (!promoDef || productNameById.size === 0) return undefined;

    try {
      const resolved = resolvePromotionComponentsForDispatch(
        promoDef,
        promotionSelections,
        productNameById
      );
      return resolved.length ? resolved : undefined;
    } catch {
      return undefined;
    }
  };

  const isPromoProduct = (productId: string) =>
    promotions.some((p) => p.promoProductId === productId && p.isActive);


  const addItemsToTabList = (old: Tab[] = []) =>
    old.map((tab) => {
      if (tab.id !== variables.tabId) return tab;

      const items: TabItem[] = [...tab.items];
      for (const entry of variables.items) {
        const entryNotes = normalizeNotes(entry.notes);
        const entryPosition = normalizeCustomerPosition(entry.customerPosition);
        const promotionComponents = resolveOptimisticPromotionComponents(
          entry.product.id,
          entry.promotionSelections
        );
        const isPromoEntry = isPromoProduct(entry.product.id);
        const hasWeight = hasPositiveWeight(entry.weightKg);
        // Weight lines are priced per kg, so structured modifiers never apply to them.
        const entryModifiers = hasWeight ? undefined : entry.modifiers;
        const entrySignature = modifierSignature(entryModifiers);
        const unitPrice = hasWeight
          ? calculateWeightLinePrice(entry.product.price, entry.weightKg)
          : lineUnitPrice(entry.product, entryModifiers);
        const quantity = hasWeight ? 1 : entry.quantity;

        // Weight and promo lines never merge into an existing row.
        const existingIndex =
          isPromoEntry || hasWeight
            ? -1
            : items.findIndex(
                (item) =>
                  item.productId === entry.product.id &&
                  normalizeNotes(item.notes) === entryNotes &&
                  normalizeCustomerPosition(item.customerPosition) === entryPosition &&
                  modifierSignature(item.modifiers) === entrySignature &&
                  !(item.promotionComponents && item.promotionComponents.length > 0) &&
                  !isWeightLine(item)
              );

        if (existingIndex >= 0) {
          items[existingIndex] = {
            ...items[existingIndex],
            quantity: items[existingIndex].quantity + quantity,
          };
        } else {
          items.push({
            id: entry.itemId,
            productId: entry.product.id,
            product: entry.product,
            quantity,
            unitPrice,
            discount: 0,
            notes: entryNotes ?? undefined,
            customerPosition: entryPosition ?? undefined,
            weightKg: hasWeight ? entry.weightKg : undefined,
            createdAt: new Date(),
            promotionComponents,
            modifiers: toTabItemModifiers(entry.itemId, entryModifiers),
          });
        }
      }

      return { ...tab, items };
    });

  queryClient.setQueryData<Tab[]>(tabsKey, addItemsToTabList);
  queryClient.setQueryData<Tab[]>(externalOrdersKey, (old) =>
    old ? addItemsToTabList(old) : old
  );

  queryClient.setQueryData<Product[]>(productsKey, (old = []) =>
    old.map((product) => {
      const totalDeduction = variables.items
        .filter((entry) => entry.product.id === product.id)
        .reduce((sum, entry) => {
          if (hasPositiveWeight(entry.weightKg)) {
            return sum + entry.weightKg;
          }
          return sum + entry.quantity;
        }, 0);
      if (totalDeduction === 0 || product.stock <= 0) return product;
      return { ...product, stock: Math.max(0, product.stock - totalDeduction) };
    })
  );

  return { previousTabs, previousProducts, previousExternalOrders, tabsKey, productsKey, externalOrdersKey };
};

/**
 * Moves items between tabs in the cached tabs query. Uses `newItemId` from the
 * snapshot payload so optimistic partial rows match the offline replay ids.
 * Returns the previous cache value for rollback.
 */
export const applyMoveItemsOptimistically = (
  queryClient: QueryClient,
  variables: MoveItemsBetweenTabsVariables
) => {
  const tabsKey = queryKeys.tabs(variables.organizationId);
  const previousTabs = queryClient.getQueryData<Tab[]>(tabsKey);

  queryClient.setQueryData<Tab[]>(tabsKey, (old = []) => {
    const next = old.map((tab) => ({ ...tab, items: [...tab.items] }));

    for (const entry of variables.items) {
      const sourceTab = next.find((tab) => tab.id === variables.sourceTabId);
      const targetTab = next.find((tab) => tab.id === variables.targetTabId);
      if (!sourceTab || !targetTab) continue;

      const itemIndex = sourceTab.items.findIndex((item) => item.id === entry.itemId);
      if (itemIndex < 0) continue;

      const item = sourceTab.items[itemIndex];

      if (entry.isFullMove) {
        sourceTab.items.splice(itemIndex, 1);
        targetTab.items.push(item);
        continue;
      }

      const isWeightPartial = isWeightPartialMove(entry);

      sourceTab.items[itemIndex] = isWeightPartial
        ? {
            ...item,
            quantity: 1,
            weightKg: entry.remainingWeightKg,
            unitPrice: entry.remainingUnitPrice,
          }
        : {
            ...item,
            quantity: item.quantity - entry.quantityToMove,
          };

      targetTab.items.push({
        ...item,
        id: entry.newItemId ?? generateUuid(),
        quantity: isWeightPartial ? 1 : entry.quantityToMove,
        unitPrice: entry.unitPrice,
        weightKg: isWeightPartial ? entry.weightKg : item.weightKg,
        createdAt: new Date(),
        promotionComponents: item.promotionComponents?.map((c) => ({ ...c })),
        modifiers: item.modifiers?.map((modifier) => ({ ...modifier })),
      });
    }

    return next;
  });

  return { previousTabs, tabsKey };
};

/**
 * Registers default mutationFns for replayable POS mutations. Must run before
 * the persisted cache is restored so rehydrated paused mutations can execute.
 */
export const registerOfflineMutationDefaults = (queryClient: QueryClient): void => {
  queryClient.setMutationDefaults(offlineMutationKeys.createTab, {
    mutationFn: (variables: CreateTabVariables) => executeCreateTab(variables),
    onSettled: (_data, _error, variables: CreateTabVariables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tabs(variables.organizationId) });
    },
  });

  queryClient.setMutationDefaults(offlineMutationKeys.addItemsToTab, {
    mutationFn: (variables: AddItemsToTabVariables) => executeAddItemsToTab(variables),
    onSettled: (_data, _error, variables: AddItemsToTabVariables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tabs(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.products(variables.organizationId) });
    },
  });

  queryClient.setMutationDefaults(offlineMutationKeys.moveItemsBetweenTabs, {
    mutationFn: (variables: MoveItemsBetweenTabsVariables) =>
      executeMoveItemsBetweenTabs(variables),
    onSettled: (_data, _error, variables: MoveItemsBetweenTabsVariables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tabs(variables.organizationId) });
    },
  });

  queryClient.setMutationDefaults(offlineMutationKeys.recordSaleAndCloseTab, {
    mutationFn: (variables: RecordSaleAndCloseTabVariables) =>
      executeRecordSaleAndCloseTab(variables),
    onSettled: (_data, _error, variables: RecordSaleAndCloseTabVariables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tabs(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.salesAll(variables.organizationId) });
    },
  });

  queryClient.setMutationDefaults(offlineMutationKeys.createElectronicTicket, {
    mutationFn: (variables: CreateElectronicTicketVariables) =>
      executeCreateElectronicTicket(variables),
    onSettled: (_data, _error, variables: CreateElectronicTicketVariables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.salesAll(variables.organizationId) });
    },
  });
};

/**
 * Resumes mutations restored from the persisted cache (queued offline before an
 * app restart), then refetches so the UI reconciles with server truth.
 * Wired to PersistQueryClientProvider's onSuccess.
 */
export const resumePausedMutationsAfterRestore = async (
  queryClient: QueryClient
): Promise<void> => {
  try {
    await ensureOrganizationClient();
  } catch {
    // Offline cold start: org client can't connect yet. Mutations stay paused
    // and resume via the online-event flow once connectivity returns.
  }
  await queryClient.resumePausedMutations();
  await queryClient.invalidateQueries();
};
